import { Order } from '@rd/types';
import {
  AutoRedemptionSettingStrategy,
  PeriodAutoRedemptionSettingStrategyOptions,
} from '@rd/types/auto-redemption-setting.type';
import { PromocodeUsage } from '@rd/types/promocode.type';

export enum MFAOutcome {
  Success = 'success',
  Failure = 'failure',
}

export enum MFARedemptionType {
  AutoRedemption = 'AutoRedemtion',
  OneTimeRedemtion = 'OneTimeRedemption',
  ShoppingCart = 'ShoppingCart',
}

export enum MFAProductType {
  ShoppingCart = 'ShoppingCart ',
  DigitalGiftCard = 'DigitalGiftCard',
  PhysicalGiftCard = 'PhysicalGiftCard',
  Cashback = 'Cashback',
  PointsTransfer = 'PointsTransfer ',
  EverydaySpend = 'EverydaySpend',
  AutoRedemptionUpdate = 'AutoRedemptionUpdate',
}

export type MFAAutoRedemptionProductType = Extract<
  MFAProductType,
  | MFAProductType.Cashback
  | MFAProductType.DigitalGiftCard
  | MFAProductType.PhysicalGiftCard
  | MFAProductType.PointsTransfer
  | MFAProductType.AutoRedemptionUpdate
>;

interface OneTimeRedemption<T extends MFAProductType, ProductMeta> {
  type: MFARedemptionType.OneTimeRedemtion;
  product: T;
  verifiedRedirectUrl: string;
  data: ProductMeta;
}

export interface AutoRedemption<
  T extends MFAAutoRedemptionProductType,
  ProductMeta,
> {
  verifiedRedirectUrl: string;
  type: MFARedemptionType.AutoRedemption;
  product: T;
  data: ProductMeta;
  isEverydaySpend?: boolean;
}

export type OneTimeRedemptionCashBack = OneTimeRedemption<
  MFAProductType.Cashback,
  {
    redemptionAmount: number; // sourceAmount
    cashAmount: number; // targetAmount
    loyaltyProgramId: string;
    promocode?: string;
    promocodeUsage?: PromocodeUsage;
  }
>;

export type OneTimeRedemptionPointsTransfer = OneTimeRedemption<
  MFAProductType.PointsTransfer,
  {
    loyaltyProgramId: string;
    membershipId: string;
    sourceAmount: number; // wl points
    targetAmount: number; // partner points
    promocode?: string;
    promocodeUsage?: PromocodeUsage;
  }
>;

export type OneTimeRedemptionEverydaySpend = OneTimeRedemption<
  MFAProductType.EverydaySpend,
  {
    loyaltyProgramId: string;
    membershipId: string;
    sourceAmount: number; // wl points
    targetAmount: number; // partner points
  }
>;

export type AutoRedemptionUpdate = AutoRedemption<
  MFAAutoRedemptionProductType,
  {
    id: string;
    pointsRequired: number;
    strategy: AutoRedemptionSettingStrategy;
    strategyOptions: PeriodAutoRedemptionSettingStrategyOptions;
  }
>;

export type AutoRedemptionCashBack = AutoRedemption<
  MFAProductType.Cashback,
  {}
>;
export type AutoRedemptionDigitalGiftCard = AutoRedemption<
  MFAProductType.DigitalGiftCard,
  {}
>;
export type AutoRedemptionPhysicalGiftCard = AutoRedemption<
  MFAProductType.PhysicalGiftCard,
  {}
>;
export type AutoRedemptionPointsTransfer = AutoRedemption<
  MFAProductType.PointsTransfer,
  {}
>;

// Shopping Cart supports Gift Cards also since the Gift Card Checkout
// Flow is based on the shopping cart for "non-shopping cart" white labels
export interface MFAShoppingCart {
  verifiedRedirectUrl: string;
  type: MFARedemptionType.ShoppingCart;
  data: Order;
}

export type MFAOneTimeRedemption =
  | OneTimeRedemptionCashBack
  | OneTimeRedemptionPointsTransfer
  | OneTimeRedemptionEverydaySpend;

export type MFAAutoRedemption =
  | AutoRedemptionCashBack
  | AutoRedemptionDigitalGiftCard
  | AutoRedemptionPhysicalGiftCard
  | AutoRedemptionPointsTransfer
  | AutoRedemptionUpdate;

export enum MFARedirectUrlKeys {
  AutoRedemptionUpdate = 'AutoRedemptionUpdate',
  CashBackAutoRedemption = 'cashBackAutoRedemption',
  CashbackOneTimeRedemption = 'cashbackOneTimeRedemption',
  EverydaySpendOneTimeRedemption = 'everydaySpendOneTimeRedemption',
  DigitalGiftCardAutoRedemption = 'digitalGiftCardAutoRedemption',
  PhysicalGiftCardAutoRedemption = 'physicalGiftCardAutoRedemption',
  PointsTransferAutoRedemption = 'pointsTransferAutoRedemption',
  PointsTransferOneTimeRedemption = 'pointsTransferOneTimeRedemption',
  EverydaySpendAutoRedemption = 'everydaySpendAutoRedemption',
  ShoppingCart = 'shoppingCart',
}

export type MFARedirectUrlMap = {
  [k in MFARedirectUrlKeys]: {
    ghRedirectUrl: string | null;
    rdRedirectUrl: string | null;
  };
};
